import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { HtmlContent } from '@/components/ui/html-content';
import { Trash2, Plus, Minus, ShoppingBag } from 'lucide-react';
import { Product } from './ProductCard';
import { useNavigate } from 'react-router-dom';

export interface CartItem extends Product {
  quantity: number;
}

interface CartProps {
  items: CartItem[];
  onUpdateQuantity: (productId: string, quantity: number) => void;
  onRemoveItem: (productId: string) => void;
  onClearCart: () => void;
}

export const Cart = ({ items, onUpdateQuantity, onRemoveItem, onClearCart }: CartProps) => {
  const navigate = useNavigate();
  const [total, setTotal] = useState(0);

  useEffect(() => {
    const calculatedTotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
    setTotal(calculatedTotal);
    
    // Debug: verifica che gli items siano ricevuti correttamente
    if (import.meta.env.DEV) {
      console.log('Cart component - Items ricevuti:', items);
      console.log('Cart component - Totale calcolato:', calculatedTotal);
    }
  }, [items]);

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <ShoppingBag className="mb-4 h-16 w-16 text-muted-foreground" />
        <h3 className="mb-2 text-xl font-semibold">Il tuo carrello è vuoto</h3>
        <p className="mb-6 text-muted-foreground">
          Inizia ad aggiungere prodotti al carrello
        </p>
        <Button onClick={() => navigate('/catalogo')}>
          Vai al Catalogo
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Il tuo carrello</h2>
        <Button variant="ghost" size="sm" onClick={onClearCart}>
          Svuota carrello
        </Button>
      </div>

      <div className="space-y-4">
        {items.map((item) => (
          <Card key={item.id}>
            <CardContent className="p-4">
              <div className="flex gap-4">
                <img
                  src={item.image}
                  alt={item.name}
                  className="h-24 w-24 rounded-md object-cover"
                />
                <div className="flex flex-1 flex-col justify-between">
                  <div>
                    <h3 className="font-semibold">{item.name}</h3>
                    {item.description && (
                      <div className="text-sm text-muted-foreground line-clamp-1">
                        <HtmlContent html={item.description} maxLines={1} />
                      </div>
                    )}
                    {item.weight && (
                      <p className="text-xs text-muted-foreground">{item.weight}</p>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => onUpdateQuantity(item.id, item.quantity - 1)}
                      >
                        <Minus className="h-4 w-4" />
                      </Button>
                      <span className="w-8 text-center text-sm font-medium">
                        {item.quantity}
                      </span>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="font-semibold">
                        €{(item.price * item.quantity).toFixed(2)}
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive"
                        onClick={() => onRemoveItem(item.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Riepilogo ordine</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Subtotale</span>
            <span>€{total.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Spedizione</span>
            <span className="text-muted-foreground">Calcolata al checkout</span>
          </div>
          <Separator />
          <div className="flex justify-between text-lg font-bold">
            <span>Totale</span>
            <span>€{total.toFixed(2)}</span>
          </div>
          <Button className="w-full" size="lg" onClick={() => navigate('/checkout')}>
            Procedi al Checkout
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

